import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Spinner } from 'react-bootstrap';
import { FaSearch, FaFilter, FaPercent, FaEdit } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { dsoApi } from '../../services/api';
import Swal from 'sweetalert2';

const GlobalCommissions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dsoList, setDsoList] = useState({ items: [], total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All');

  // Fetch DSO list from API
  const fetchDSOList = async () => {
    setLoading(true);
    try {
      // Build parameters for the API call
      const params = {
        skip: (currentPage - 1) * entriesPerPage,
        limit: entriesPerPage
      };
      
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      
      if (statusFilter !== 'All') {
        params.status = statusFilter;
      }
      
      // Use the dsoApi client to fetch the data
      const response = await dsoApi.getDsoList(params);
      
      setDsoList(response.data);
      setTotalPages(Math.ceil(response.data.total / entriesPerPage));
    } catch (error) {
      console.error("Error fetching DSO list:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load DSO list. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Fetch data when pagination, search, or filters change
  useEffect(() => {
    fetchDSOList();
  }, [currentPage, entriesPerPage, debouncedSearchTerm, statusFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, entriesPerPage]);

  // Handle navigation to DSO commissions page
  const navigateToCommissions = (dsoId) => {
    navigate(`/admin/dso/${dsoId}/commissions`);
  };

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Get visible page numbers for pagination
  const getVisiblePageNumbers = () => {
    if (totalPages <= 3) {
      return pageNumbers;
    }
    
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    
    return [currentPage - 1, currentPage, currentPage + 1];
  };

  return (
    <Container fluid className="p-3">
      <Row className="mb-2">
        <Col>
          <h4 className="mb-1">Global Commissions Management</h4>
          <p className="text-muted small mb-1">Manage commission settings for all DSO partners</p>
        </Col>
      </Row>
      
      <Row className="mb-2">
        <Col md={4} lg={4}>
          <InputGroup className="mb-2">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search by DSO name, email or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2} lg={2}>
          <InputGroup className="mb-2">
            <InputGroup.Text title="Status Filter"><FaFilter /></InputGroup.Text>
            <Form.Select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="All">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Verified">Verified</option>
              <option value="Deactive">Deactive</option>
            </Form.Select>
          </InputGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <Table striped hover responsive size="sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>DSO Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <Spinner animation="border" role="status" size="sm" className="me-2" />
                        Loading DSO list...
                      </td>
                    </tr>
                  ) : dsoList.items.length > 0 ? (
                    dsoList.items.map((dso) => (
                      <tr key={dso.id}>
                        <td>{dso.id}</td>
                        <td>{dso.name}</td>
                        <td>{dso.email}</td>
                        <td>
                          <span className={`badge ${
                            dso.status === 'Verified' ? 'bg-success' : 
                            dso.status === 'Pending' ? 'bg-warning text-dark' : 
                            dso.status === 'Deactive' ? 'bg-danger' : 'bg-primary'
                          }`}>
                            {dso.status}
                          </span>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigateToCommissions(dso.id)}
                          >
                            <FaPercent className="me-1" /> Manage Commissions
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-3">
                        No DSO partners found matching the search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                <div className="mb-2">
                  <span>Show </span>
                  <Form.Select
                    style={{ width: 'auto', display: 'inline-block' }}
                    className="mx-2"
                    value={entriesPerPage}
                    onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </Form.Select>
                  <span> entries per page</span>
                </div>

                <Pagination className="mb-0">
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  />
                  
                  {getVisiblePageNumbers().map(number => (
                    <Pagination.Item 
                      key={number} 
                      active={number === currentPage}
                      onClick={() => setCurrentPage(number)}
                    >
                      {number}
                    </Pagination.Item>
                  ))}
                  
                  <Pagination.Next 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>

                <div className="mb-2 text-secondary">
                  Showing {dsoList.items.length > 0 ? ((currentPage - 1) * entriesPerPage) + 1 : 0} to {((currentPage - 1) * entriesPerPage) + dsoList.items.length} of {dsoList.total} entries
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default GlobalCommissions;